<template>
  <div id="wrapper">
    <Header />

    <main v-show="isLoaded">
      <Signup v-if="currentComponent === 'Signup'" class="new-app"
        :email-address-to-verify="urlParams.userId"
        :token-to-verify="urlParams.token"
        :trial-type="trialType"
        :misc-url-params="urlParams.misc === null ? null : { ...urlParams.misc }"
        @loaded="value => {isLoaded = value}"
        @processing="(value, value2, value3, value4) => {setProcessing(value, value2, value3, value4)}"
        @verified="emailAddress => userId = emailAddress"
        @registered="accountRegistered"
      />

      <Login v-else-if="currentComponent === 'Login'" class="new-app"
        :user-id="userId"
        :device-info="deviceInfo"
        :device-id="deviceId"
        @processing="value => {isProcessing = value}"
        @alert="(title, message) => setAlert(title, message)"
        @loggedIn="(userId, loginToken) => setLoggedIn(userId, loginToken)"
      />

      <Account v-else-if="isCampaignSuccessFinished && currentComponent === 'Account'" class="app"
        :user-id="userId"
        :login-token="loginToken"
        :is-production="isProduction"
        :is-loaded="isLoaded"
        :url-path="urlPath"
        :stripe="stripe"
        :is-stripe-dev="isStripeDev"
        :is-i-phone="isIPhone"
        :is-campaign-success="isCampaignSuccess"
        @loaded="value => {isLoaded = value}"
        @processing="(value, value2, value3, value4) => {setProcessing(value, value2, value3, value4)}"
        @alert="(title, message, messageWithLineBreaks) => setAlert(title, message, messageWithLineBreaks)"
        @loggedIn="setLoginCookies"
        @logout="logout"
      />
    </main>

    <Footer />

    <AlertOverlay
      :show-overlay="showAlertOverlay"
      :title="alertTitle"
      :message="alertMessage"
      :message-with-line-breaks="messageWithLineBreaks"
      @close="() => {showAlertOverlay = false}"
    />

    <Processing
      :is-processing="isProcessing"
      :show-message="showProcessingMessage"
      :message1="processingMessage1"
      :message2="processingMessage2"
    />
  </div>
</template>

<script>
import { createUUID } from '@/api/common_helpers.js'
import {
  MEMBER_REGISTRATION_ACTION_NAMES,
  TRIAL_LIGHT_ACTION_NAMES,
  TRIAL_EL_ACTION_NAMES,
  TRIAL_CAMPAIGN_ACTION_NAMES,
  CAMPAIGN_SUCCESS_ACTION_NAMES,
  IS_PRODUCTION,
  IS_STRIPE_DEV,
  LIGHT_TRIAL_TYPE_KEY,
  ELEARNING_TRIAL_TYPE_KEY,
  CAMPAIGN_TRIAL_TYPE_KEY
} from '@/main.js'
import Header from '@/components/helpers/Header.vue'
import Signup from '@/components/Signup.vue'
import Login from '@/components/Login.vue'
import Account from '@/components/Account.vue'
import Footer from '@/components/helpers/Footer.vue'
import AlertOverlay from '@/components/helpers/AlertOverlay.vue'
import Processing from '@/components/helpers/Processing.vue'
import {loadStripe} from '@stripe/stripe-js';
import '@stripe/stripe-js';

export default {
  name: 'App',

  components: {
    Header,
    Signup,
    Login,
    Account,
    Footer,
    AlertOverlay,
    Processing
  },

  data() {
    return {
      currentComponent: "Login",

      urlPath: [],
      urlParams: {userId: "", token: "", trialType: "", misc: null},
      trialTypeBasedOnUrlOrigin: null,

      isLoaded: false,
      isProcessing: false,
      isSignup: false,
      isCampaignSuccess: false,
      isCampaignSuccessFinished: true,
      showProcessingMessage: false,
      processingMessage1: null,
      processingMessage2: null,
      isProduction: IS_PRODUCTION,
      isIPhone: false,

      showAlertOverlay: false,
      alertTitle: "Error",
      alertMessage: "正しいメールアドレス・パスワードを入力してください。",
      messageWithLineBreaks: false,

      userId: "",
      deviceInfo: "",
      deviceId: "",
      loginToken: "",

      stripe: null,
    }
  },

  computed: {
    urlAction() {
      return this.urlPath[0]
    },

    trialType() {
      return this.urlParams.trialType || this.trialTypeBasedOnUrlOrigin || 'light'
    },

    isStripeDev() {
      return IS_STRIPE_DEV
    }
  },

  async created() {
    this.processUrl()
    this.getOrCreateDeviceId()
    this.deviceInfo = navigator.userAgent
    this.isIPhone = navigator.userAgent.includes("iPhone")
    this.stripe = await loadStripe(!IS_STRIPE_DEV ? "pk_live_OVK8oRzxTatlJRaUuYuNkwgE00YpRKafur" : "pk_test_wlWcp2p3w7haw9czpFyXRk8v009qNJlSGs")
    if (this.isSignup) {
      document.title = "登録 | LanCulマイページ"
      this.currentComponent = "Signup"
    } else {
      document.title = "ログイン | LanCulマイページ"
      this.tryAutoLogin()
    }
    if (this.isCampaignSuccess) {
      this.isProcessing = true
      setTimeout(() => {
        this.isProcessing = false
        this.isLoaded = true
        this.isCampaignSuccessFinished = true
      }, 500)
    } else {
      this.isLoaded = true
    }
  },

  methods: {
    processUrl() {
      const urlParams = new URLSearchParams(window.location.search)
      if (window.location.pathname) {
        this.urlPath = window.location.pathname.split('/').slice(1).filter(string => string !== "")
        this.getUrlOrigin()
      }

      var miscUrlParams = {}
      var isMiscUrlParamPresent = false
      const entries = urlParams.entries()
      var entry = entries.next()
      while (!entry.done) {
        isMiscUrlParamPresent = true
        miscUrlParams = {
          ...miscUrlParams,
          [entry.value[0]]: entry.value[1]
        }
        entry = entries.next()
      }

      this.urlParams = {
        userId: (urlParams.get('userId') || urlParams.get('userid') || '').replace(' ', '+'),
        token: urlParams.get('token') || '',
        trialType: urlParams.get('trialType'),
        misc: isMiscUrlParamPresent ? miscUrlParams : null
      }
    },

    getUrlOrigin() {
      var urlOrigin = window.location.origin
      var isSignup = true
      if (MEMBER_REGISTRATION_ACTION_NAMES.includes(this.urlAction)) {
        urlOrigin = MEMBER_REGISTRATION_ACTION_NAMES[0]
      } else if (TRIAL_LIGHT_ACTION_NAMES.includes(this.urlAction)) {
        this.trialTypeBasedOnUrlOrigin = LIGHT_TRIAL_TYPE_KEY
        urlOrigin = TRIAL_LIGHT_ACTION_NAMES[0]
      } else if (TRIAL_EL_ACTION_NAMES.includes(this.urlAction)) {
        this.trialTypeBasedOnUrlOrigin = ELEARNING_TRIAL_TYPE_KEY
        urlOrigin = TRIAL_EL_ACTION_NAMES[0]
      } else if (TRIAL_CAMPAIGN_ACTION_NAMES.includes(this.urlAction)) {
        this.trialTypeBasedOnUrlOrigin = CAMPAIGN_TRIAL_TYPE_KEY
        urlOrigin = TRIAL_CAMPAIGN_ACTION_NAMES[0]
      } else if (CAMPAIGN_SUCCESS_ACTION_NAMES.includes(this.urlAction)) {
        this.trialTypeBasedOnUrlOrigin = CAMPAIGN_SUCCESS_ACTION_NAMES[0]
        urlOrigin = CAMPAIGN_SUCCESS_ACTION_NAMES[0]
        this.isCampaignSuccess = true
        this.isCampaignSuccessFinished = false
        isSignup = false
      } else {
        isSignup = false
      }
      this.isSignup = isSignup
      return urlOrigin
    },

    getOrCreateDeviceId() {
      this.deviceId = this.$cookie.getCookie("_ead313")
      if (!this.deviceId) {
        this.deviceId = createUUID()
        this.$cookie.setCookie("_ead313", this.deviceId, {
          expire: '5y'
        })
      }
    },

    tryAutoLogin() {
      if (this.urlParams.userId && this.urlParams.token) {
        this.userId = this.urlParams.userId
        this.loginToken = this.urlParams.token
        document.title = "マイページ | LanCulマイページ"
        this.currentComponent = "Account"
      } else {
        const userId = this.$cookie.getCookie("userId")
        const loginToken = this.$cookie.getCookie("loginToken")
        if (userId && loginToken) {
          this.userId = userId
          this.loginToken = loginToken
          document.title = "マイページ | LanCulマイページ"
          this.currentComponent = "Account"
        } else {
          if (this.urlPath && this.urlPath[0] === CAMPAIGN_SUCCESS_ACTION_NAMES[0]) {
            window.history.pushState("", "", "./")
          }
        }
      }
    },

    accountRegistered() {
      document.title = "ログイン | LanCulマイページ"
      window.history.pushState("", "", window.location.origin)
      this.currentComponent = 'Login'
    },

    setAlert(title, message, messageWithLineBreaks) {
      this.alertTitle = title
      this.alertMessage = message
      this.messageWithLineBreaks = messageWithLineBreaks !== false
      this.showAlertOverlay = true
    },

    setProcessing(value, value2, value3, value4) {
      this.isProcessing = value
      this.showProcessingMessage = value2 === true
      this.processingMessage1 = value3 || ''
      this.processingMessage2 = value4 || ''
    },

    setLoggedIn(userId, loginToken) {
      this.userId = userId
      this.loginToken = loginToken
      document.title = "マイページ | LanCulマイページ"
      window.history.pushState("", "", window.location.origin)
      this.currentComponent = "Account"
      this.isLoaded = false
    },

    setLoginCookies() {
      this.$cookie.setCookie("userId", this.userId, {
        expire: '7d'
      })
      this.$cookie.setCookie("loginToken", this.loginToken, {
        expire: '7d'
      })
    },

    logout() {
      this.$cookie.removeCookie("loginToken")
      this.$cookie.removeCookie("userId")
      document.title = "ログイン | LanCulマイページ"
      this.currentComponent = "Login"
      this.userId = ""
      this.loginToken = ""
    }
  }
}
</script>

<style>
* {
  font-family: 'Hiragino Sans' !important;
}

body {
  margin: 0;
}

p {
  margin-block-start: 0;
  margin-block-end: 0;
}
</style>

<style scoped>
#wrapper {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

#wrapper main {
  width: 100%;
}

.app {
  max-width: 800px;
  margin: 0 auto;
}

.new-app {
  max-width: 800px;
  margin: 0 auto;
}
</style>
